import { Entity } from '../../../../domain/entity';
import { validationProvider } from '../../../../infrastructure/validation/validation.provider';
import { AssetCreatedEvent } from './event/asset-created.event';
import { AssetUpdatedEvent } from './event/asset-updated.event';
import { FieldSyncAssetUpdatedEvent, } from './event/field-sync-asset-updated.event';
import { AssetDeletedEvent } from './event/asset-deleted.event';
import { AssetPurgedEvent } from './event/asset-purged.event';
import { AssetArchivedEvent } from './event/asset-archived.event';
import { AssetUnarchivedEvent } from './event/asset-unarchived.event';
import { AssetAddPhotoEvent } from './event/asset-add-photo.event';
import { AssetRemovePhotoEvent, } from './event/asset-remove-photo.event';
import projections from './projections-index';
import { FieldTag, DataType } from '../../../field/field/domain/field.interface';
import { AssetClassEntity } from '../../asset-class/domain/asset-class.model';
import { getLogger } from '../../../../infrastructure/log/logger';
import { ApplicationError, ErrorCode } from '../../../../infrastructure/error/application-error';
const logger = getLogger();
export class AssetEntity extends Entity {
    constructor(properties, fields) {
        super(properties.id, properties.createdAt);
        this.fields = fields;
        this._assetClassId = validationProvider.validateAndTransformId(properties.assetClassId, {
            name: 'Asset Class ID',
            required: true,
        });
        this._attributes = validationProvider.validateAndTransformObject(properties.attributes, {
            name: 'Attributes',
            transformNullishToEmptyObject: true,
            transformPropertiesEmptyStringToNull: true,
        });
        this.validateAndTransformAttributeValues(this._attributes);
        this._geometry = this.getGeometryFromAsset(fields);
        this._sourceSystem = properties.sourceSystem;
        this._sourceTool = properties.sourceTool;
    }
    validateAndTransformAttributeValues(attributes) {
        for (const [attributeId, attributeValue] of Object.entries(attributes)) {
            const field = this.fields.find(field => field.id === attributeId);
            if (!field) {
                const message = `Field with ID ${attributeId} not found in asset class`;
                throw new ApplicationError({
                    messageExternal: message,
                    code: ErrorCode.ERR_INTERNAL_SERVER_ERROR,
                });
            }
            if (field.dataType === DataType.TIMESTAMP) {
                if (attributeValue === '') {
                    attributes[attributeId] = null;
                }
            }
        }
    }
    create(metadata) {
        return new AssetCreatedEvent({
            id: this.id,
            assetClassId: this.assetClassId,
            attributes: this.attributes,
            sourceSystem: this.sourceSystem,
            sourceTool: this.sourceTool,
        }, metadata);
    }
    update(attributes, metadata) {
        const validatedAttributes = validationProvider.validateAndTransformObject(attributes, {
            name: 'Attributes',
            transformNullishToEmptyObject: true,
            transformPropertiesEmptyStringToNull: true,
        });
        this.validateAndTransformAttributeValues(validatedAttributes);
        this.patchAttributes(validatedAttributes);
        return new AssetUpdatedEvent({
            id: this.id,
            assetClassId: this.assetClassId,
            attributes: {
                ...validatedAttributes,
            },
        }, metadata);
    }
    fieldSyncUpdate(attributes, metadata) {
        const validatedAttributes = validationProvider.validateAndTransformObject(attributes, {
            name: 'Attributes',
            transformNullishToEmptyObject: true,
            transformPropertiesEmptyStringToNull: true,
        });
        this.validateAndTransformAttributeValues(validatedAttributes);
        this.patchAttributes(validatedAttributes);
        return new FieldSyncAssetUpdatedEvent({
            id: this.id,
            assetClassId: this.assetClassId,
            attributes: {
                ...validatedAttributes,
            },
        }, metadata);
    }
    delete(metadata) {
        return new AssetDeletedEvent({
            id: this.id,
            assetClassId: this.assetClassId,
            deleted: true,
        }, metadata);
    }
    purge(metadata) {
        return new AssetPurgedEvent({
            id: this.id,
            assetClassId: this.assetClassId,
            purged: true,
        }, metadata);
    }
    archive(metadata) {
        return new AssetArchivedEvent({
            id: this.id,
            assetClassId: this.assetClassId,
            archived: true,
        }, metadata);
    }
    unarchive(metadata) {
        return new AssetUnarchivedEvent({
            id: this.id,
            assetClassId: this.assetClassId,
            archived: false,
        }, metadata);
    }
    addPhotos(photos, metadata) {
        const updatedPhotos = this.createPhotoListWithAdds(photos);
        this.patchAttributes({ [photos[0].tp3FieldId]: updatedPhotos });
        return new AssetAddPhotoEvent({
            id: this.id,
            assetClassId: this.assetClassId,
            photos,
        }, metadata);
    }
    removePhotos(photos, metadata) {
        const photosByField = photos.reduce((acc, photo) => {
            if (!acc[photo.fieldId]) {
                acc[photo.fieldId] = [];
            }
            acc[photo.fieldId].push(photo);
            return acc;
        }, {});
        const attributeUpdates = {};
        for (const [fieldId, fieldPhotos] of Object.entries(photosByField)) {
            const updatedPhotos = this.createPhotoListAfterRemoval(fieldId, fieldPhotos);
            attributeUpdates[fieldId] = updatedPhotos;
        }
        this.patchAttributes(attributeUpdates);
        return new AssetRemovePhotoEvent({
            id: this.id,
            assetClassId: this.assetClassId,
            photos,
        }, metadata);
    }
    createPhotoListWithAdds(photos) {
        const photoKey = photos[0].tp3FieldId;
        const { [photoKey]: currentPhotos } = this._attributes;
        const newPhotos = photos.map(photo => photo.photoBase64);
        if (!currentPhotos || JSON.parse(String(currentPhotos)).length === 0) {
            return JSON.stringify(newPhotos);
        }
        const currentPhotosArray = JSON.parse(String(currentPhotos));
        const updatedPhotos = [...new Set([...currentPhotosArray, ...newPhotos])];
        return JSON.stringify(updatedPhotos);
    }
    createPhotoListAfterRemoval(fieldId, photos) {
        const { [fieldId]: currentPhotos } = this._attributes;
        const removedPhotoIds = photos.map(photo => photo.photoId);
        if (!currentPhotos || JSON.parse(String(currentPhotos)).length === 0) {
            return '[]';
        }
        const currentPhotosArray = JSON.parse(String(currentPhotos));
        const updatedPhotos = currentPhotosArray.filter((currentPhoto) => !removedPhotoIds.includes(currentPhoto));
        return JSON.stringify(updatedPhotos);
    }
    patchAttributes(attributes) {
        this._attributes = {
            ...this._attributes,
            ...attributes,
        };
    }
    putAttributes(attributes) {
        this._attributes = {
            ...attributes,
        };
    }
    getGeometryFromAsset(fields) {
        const latField = AssetClassEntity.getFieldFromTag(fields, FieldTag.LATITUDE);
        const lngField = AssetClassEntity.getFieldFromTag(fields, FieldTag.LONGITUDE);
        if (!latField || !lngField) {
            const message = 'AssetClass item must have lat and lng fields';
            logger.error(message);
            throw new Error(message);
        }
        const geometry = {
            type: 'Point',
            coordinates: [this.attributes[lngField.id], this.attributes[latField.id]],
        };
        return geometry;
    }
    static restoreAggregateState(events, snapshot) {
        return super.buildAggregateState(events, projections, snapshot);
    }
    get assetClassId() {
        return this._assetClassId;
    }
    get attributes() {
        return this._attributes;
    }
    get geometry() {
        return this._geometry;
    }
    get sourceSystem() {
        return this._sourceSystem;
    }
    get sourceTool() {
        return this._sourceTool;
    }
}
//# sourceMappingURL=asset.model.js.map